import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { ReceiptText, RefreshCw, Search } from "lucide-react";
import { useSelector } from "react-redux";

const API = process.env.REACT_APP_API_URL;
const API_ORIGIN = (API || "").replace(/\/api\/?$/, "");

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const documentUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${API_ORIGIN}${value.startsWith("/") ? value : `/${value}`}`;
};

const paymentDetails = (row) => {
  const due = Number(row.totalDue) ||
    (Number(row.approvedAmount) || 0) +
      (Number(row.feLoginAmount) || 0) +
      (Number(row.feDisbursementAmount) || 0);
  const paid = Number(row.paidAmount) || 0;
  const outstanding = Math.max(due - paid, 0);
  const status = row.paid === true ? "paid" : paid > 0 ? "outstanding" : "pending";
  return { due, paid, outstanding, status };
};

const statusStyle = {
  paid: "border-green-200 bg-green-50 text-green-700",
  outstanding: "border-amber-200 bg-amber-50 text-amber-700",
  pending: "border-red-200 bg-red-50 text-red-700",
};

const statusLabel = {
  paid: "Full Payment",
  outstanding: "Outstanding",
  pending: "Pending",
};

function SummaryCard({ label, value, helper }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-2 text-xl font-semibold text-gray-900 sm:text-2xl">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{helper}</p>
    </div>
  );
}

export default function Payment() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    totalDue: 0,
    totalPaid: 0,
    outstanding: 0,
    completed: 0,
    records: 0,
  });

  const fetchPayments = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = userInfo?.token
        ? { Authorization: `Bearer ${userInfo.token}` }
        : {};
      const { data } = await axios.get(`${API}/payments`, {
        headers,
        params: {
          page,
          limit: pageSize,
          q: debouncedQuery || undefined,
          status,
        },
      });
      setRows(data.items || []);
      setTotalPages(Math.max(Number(data.pages) || 1, 1));
      setSummary(data.summary || {});
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Payments failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, pageSize, status]);

  useEffect(() => {
    if (userInfo?.token) fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.token, page, pageSize, debouncedQuery, status]);

  return (
    <div className="min-h-screen bg-gray-50 py-3 sm:p-5">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Payments</h1>
          <p className="mt-1 text-sm text-gray-500">Track paid and outstanding enquiry fees.</p>
        </div>
        <button
          type="button"
          onClick={fetchPayments}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Total Due" value={money(summary.totalDue)} helper={`${summary.records || 0} payment records`} />
        <SummaryCard label="Total Paid" value={money(summary.totalPaid)} helper={`${summary.completed || 0} fully paid`} />
        <SummaryCard label="Outstanding" value={money(summary.outstanding)} helper="Pending collection" />
      </div>

      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-2.5 text-gray-400" size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search merchant, case, or mobile"
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label="Filter payments by status"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
        >
          <option value="all">All Payments</option>
          <option value="pending">Pending</option>
          <option value="full-paid">Full Paid</option>
          <option value="outstanding">Outstanding</option>
        </select>
        <select
          value={pageSize}
          onChange={(event) => setPageSize(Number(event.target.value))}
          aria-label="Rows per page"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
        >
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>{size} / page</option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1320px]">
            <thead className="bg-gray-50">
              <tr>
                {["S.No", "Merchant", "Case", "Mobile", "Lead Amount", "Login Amount", "Disbursement Amount", "Total Due", "Paid", "Outstanding", "Status", "Last Payment", "Proof"].map((heading) => (
                  <th key={heading} className="border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={13} className="px-4 py-14 text-center text-sm text-gray-500">Loading payments...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-14 text-center text-sm text-gray-500">No payment records found.</td></tr>
              ) : rows.map((row, index) => {
                const details = paymentDetails(row);
                const proof = documentUrl(row.paymentProofImage);
                return (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">{(page - 1) * pageSize + index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.merchantName || row.merchant?.name || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.caseName || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.mobileNumber || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{money(row.approvedAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{money(row.feLoginAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{money(row.feDisbursementAmount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{money(details.due)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-green-700">{money(details.paid)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-red-700">{money(details.outstanding)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyle[details.status]}`}>
                        {statusLabel[details.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.lastPaymentAt ? dayjs(row.lastPaymentAt).format("DD MMM YYYY") : "-"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {proof ? (
                        <a
                          href={proof}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 no-underline transition-colors hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800 hover:no-underline"
                        >
                          <ReceiptText size={14} strokeWidth={2.2} />
                          <span>View Proof</span>
                        </a>
                      ) : (
                        <span className="inline-block w-full text-center text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-3 sm:px-4">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={page === 1}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
              disabled={page === totalPages}
              className="rounded-lg border border-gray-300 px-3 py-1 text-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
